import React, { useState, useRef } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, X, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEP_UPLOAD = 'upload';
const STEP_MAP = 'map';
const STEP_DONE = 'done';

const CANONICAL_LABELS = {
  '': '— ignore —',
  name: 'Full Name',
  first_name: 'First Name',
  last_name: 'Last Name',
  phone: 'Phone',
  email: 'Email',
  birthday: 'Birthday',
  gender: 'Gender',
  notes: 'Notes',
  tags: 'Tags',
  preferred_staff: 'Preferred Staff',
  address: 'Address',
  customer_since: 'Customer Since',
};

const fieldOptions = Object.entries(CANONICAL_LABELS);

const ClientImport = ({ onComplete }) => {
  const { sessionToken } = useUnifiedAuth();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(STEP_UPLOAD);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);   // server preview response
  const [mapping, setMapping] = useState({});      // input_header → canonical
  const [duplicateStrategy, setDuplicateStrategy] = useState('merge');
  const [result, setResult] = useState(null);
  const [filename, setFilename] = useState('');

  const token = sessionToken || localStorage.getItem('sessionToken');

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    setFilename(file.name);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/clients/import/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error((await res.json()).detail || `Upload failed (${res.status})`);
      const data = await res.json();
      setPreview(data);
      setMapping({ ...(data.detected_mapping || {}) });
      setStep(STEP_MAP);
    } catch (e) {
      setError(e.message || 'Failed to read file');
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    setError(null);
    setBusy(true);
    try {
      // Strip "ignore" mappings (empty string) before sending
      const cleanMapping = {};
      Object.entries(mapping).forEach(([k, v]) => { if (v) cleanMapping[k] = v; });

      const res = await fetch(`${BACKEND_URL}/api/provider/clients/import/commit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: preview.filename,
          mapping: cleanMapping,
          rows: preview.rows || preview.sample_rows || [],
          duplicate_strategy: duplicateStrategy,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || `Import failed (${res.status})`);
      const data = await res.json();
      setResult(data);
      setStep(STEP_DONE);
      if (onComplete) onComplete(data.summary);
    } catch (e) {
      setError(e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(STEP_UPLOAD);
    setPreview(null);
    setMapping({});
    setResult(null);
    setError(null);
    setFilename('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="client-import-panel">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            Import Clients from Another CRM
          </h3>
          <p className="text-sm text-gray-500 max-w-2xl">
            Move your client list over from Fresha, Booksy, Mindbody, Square Appointments, Zenoti,
            Vagaro or any system that can export to CSV / Excel. We auto-detect column names,
            de-duplicate on phone + email, and never email your clients automatically.
          </p>
        </div>
        <a
          href={`${BACKEND_URL}/api/provider/clients/export.csv`}
          className="inline-flex items-center gap-2 text-sm text-blue-700 hover:bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg whitespace-nowrap"
          data-testid="client-export-link"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            // Use fetch w/ token then trigger download (the link doesn't carry the auth header)
            e.preventDefault();
            fetch(`${BACKEND_URL}/api/provider/clients/export.csv`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.blob()).then(blob => {
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'enayahub-clients.csv';
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
            });
          }}
        >
          <Download className="w-4 h-4" /> Export my list
        </a>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm" data-testid="client-import-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Step 1: Upload ────────────────────────────────────────────── */}
      {step === STEP_UPLOAD && (
        <div className="space-y-4">
          <div
            data-testid="client-import-dropzone"
            className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
          >
            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-gray-700 font-medium">Drop your CSV or Excel file here</p>
            <p className="text-xs text-gray-500 mt-1">or click to browse — max 10 MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid="client-import-file-input"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
          </div>
          {busy && (
            <div className="flex items-center justify-center gap-2 text-gray-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading file…
            </div>
          )}

          <details className="text-xs text-gray-500 mt-2">
            <summary className="cursor-pointer hover:text-gray-700">Which columns should the file have?</summary>
            <div className="mt-2 pl-4 space-y-1">
              <p>Required: at least a name and one of (phone, email).</p>
              <p>Optional: birthday, gender, notes, tags, preferred staff, address, customer-since.</p>
              <p>Headers like "First Name", "Mobile", "DOB" are auto-detected — you can override on the next screen.</p>
            </div>
          </details>
        </div>
      )}

      {/* ── Step 2: Column mapping + duplicate strategy ────────────────── */}
      {step === STEP_MAP && preview && (
        <div className="space-y-5" data-testid="client-import-mapping">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-700 flex-shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-medium text-blue-900">{filename}</div>
              <div className="text-blue-700/80">
                {preview.total_rows} rows · {preview.headers.length} columns detected
              </div>
            </div>
            <button
              onClick={reset}
              className="text-blue-700 hover:bg-blue-100 rounded-lg p-1"
              data-testid="client-import-cancel-btn"
              title="Pick a different file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Column mapping</h4>
            <p className="text-xs text-gray-500 mb-3">
              We auto-matched what we could — adjust below if anything is off, or set to "ignore" to skip.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0 text-sm font-mono bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 truncate" title={h}>
                    {h || '(blank)'}
                  </div>
                  <span className="text-gray-400">→</span>
                  <select
                    className="flex-1 min-w-0 text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white"
                    data-testid={`client-import-map-${h.replace(/\s+/g, '-').toLowerCase()}`}
                    value={mapping[h] || ''}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                  >
                    {fieldOptions.map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-gray-800 mb-2">If a client already exists</h4>
            <div className="space-y-2">
              {[
                { v: 'merge', t: 'Update existing — match on phone or email, merge any new fields (recommended)' },
                { v: 'skip',  t: 'Skip duplicates — only insert truly new clients' },
                { v: 'create', t: 'Always create — even if a duplicate exists (clean up later)' },
              ].map(o => (
                <label key={o.v} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="dupe-strategy"
                    value={o.v}
                    checked={duplicateStrategy === o.v}
                    onChange={(e) => setDuplicateStrategy(e.target.value)}
                    data-testid={`client-import-dupe-${o.v}`}
                    className="mt-1"
                  />
                  <span className="text-gray-700">{o.t}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleCommit}
              disabled={busy}
              data-testid="client-import-commit-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-lg shadow-sm flex items-center gap-2 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import {preview.total_rows} rows
            </button>
            <button
              onClick={reset}
              disabled={busy}
              className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Done ──────────────────────────────────────────────── */}
      {step === STEP_DONE && result && (
        <div className="space-y-4" data-testid="client-import-result">
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-semibold text-emerald-900">Import complete</div>
              <div className="text-sm text-emerald-800/80">
                {result.summary.filename}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: 'created', label: 'Created', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
              { k: 'updated', label: 'Updated', color: 'text-blue-700 bg-blue-50 border-blue-200' },
              { k: 'skipped', label: 'Skipped', color: 'text-gray-700 bg-gray-50 border-gray-200' },
              { k: 'invalid', label: 'Invalid',  color: 'text-amber-700 bg-amber-50 border-amber-200' },
            ].map(m => (
              <div
                key={m.k}
                className={`border rounded-lg p-3 text-center ${m.color}`}
                data-testid={`client-import-stat-${m.k}`}
              >
                <div className="text-2xl font-bold">{result.summary[m.k]}</div>
                <div className="text-xs uppercase tracking-wide">{m.label}</div>
              </div>
            ))}
          </div>

          {result.invalid_rows && result.invalid_rows.length > 0 && (
            <details className="bg-amber-50 border border-amber-200 rounded-lg p-3" open>
              <summary className="cursor-pointer text-sm font-medium text-amber-900">
                {result.invalid_rows.length} row{result.invalid_rows.length === 1 ? '' : 's'} couldn't be imported
              </summary>
              <ul className="mt-2 text-xs text-amber-900 space-y-1 max-h-56 overflow-y-auto">
                {result.invalid_rows.map((r, i) => (
                  <li key={i} className="font-mono">
                    Row {r.row_number}: {r.errors.join('; ')}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="flex gap-2 pt-2">
            <button
              onClick={reset}
              data-testid="client-import-again-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
            >
              Import another file
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientImport;
